/**
 * La figure v2 en SVG TEXTE — pour tout ce qui n'est pas l'écran de
 * l'application : le document imprimable d'une copie (`copieVersHtml`, puis
 * le PDF) et la page de validation de la banque
 * (`tools/generation/echantillon.mjs`).
 *
 * Même partage des rôles que `<FigureV2>` (application mobile) : le moteur
 * (`dessinerPlan`, `dessinerEspace`) CALCULE la liste d'affichage ; ce fichier
 * la RECOPIE, primitive par primitive, sans aucune géométrie. Seuls la
 * couleur et le cadre sont choisis ici.
 *
 * ── Les couleurs ───────────────────────────────────────────────────────────
 *
 * En paramètre, rôle par rôle (`PaletteSvg`). Par défaut, celles de
 * l'impression (`PALETTE_IMPRESSION`) : de l'encre presque noire, des gris, un
 * accent — des valeurs littérales, aucune variable CSS : un PDF n'a pas le
 * thème de l'application. Une page qui a ses variables (l'échantillon) peut
 * les passer en couleurs.
 *
 * ── Épaisseurs, tirets, liseré ─────────────────────────────────────────────
 *
 * Les valeurs de `figureV2-affichage.ts` (application mobile), en unités de
 * la liste d'affichage : le papier et l'écran tracent la même figure.
 *
 * ── Ce que la chaîne contient ──────────────────────────────────────────────
 *
 * Un élément `<svg>` à poser dans un document HTML (sans `xmlns` : le HTML
 * n'en veut pas, et un document autonome ne va rien chercher ailleurs). Tout
 * texte de la figure est échappé, attributs compris. `autonome` (atelier
 * graphique, doc 26 § 1.5) ajoute `xmlns` et un fond opaque : le SVG se
 * rastérise alors seul, hors d'une page HTML.
 *
 * ── Les tons ───────────────────────────────────────────────────────────────
 *
 * Une primitive qui porte un `ton` (énoncé, élève, attendu, écart) prend la
 * couleur de ce ton au lieu de celle de son calque. Aucune figure de la banque
 * n'en porte : elles sont peintes exactement comme avant.
 *
 * Fonctions pures, sans dépendance.
 */
import { dessinerEspace } from './vue3d.js';
import { EPAISSEUR_CREUX_V2, FACTEUR_ARC_DESIGNE_V2, dessinerPlan, normaliserFigureV2, } from './figure-v2.js';
/** Les trois encres de l'impression : noir, gris, un accent (celui du document de la copie). */
export const ENCRES_IMPRESSION = {
    encre: '#1b2230',
    gris: '#5a6372',
    grisClair: '#c5ccd4',
    accent: '#0b7a88',
    fond: '#ffffff',
};
/**
 * Les tons sur le papier (doc 26, § 1.5 et § 6.1) : l'énoncé en gris
 * ardoise, l'élève en accent, l'attendu en vert, l'écart en orange.
 */
export const TONS_IMPRESSION = {
    enonce: ENCRES_IMPRESSION.gris,
    eleve: ENCRES_IMPRESSION.accent,
    attendu: '#2f7d3a',
    ecart: '#c05a14',
};
/** La palette par défaut : celle du papier. */
export const PALETTE_IMPRESSION = {
    calque: {
        grille: ENCRES_IMPRESSION.grisClair,
        axe: ENCRES_IMPRESSION.gris,
        figure: ENCRES_IMPRESSION.accent,
        courbe: ENCRES_IMPRESSION.encre,
        cote: ENCRES_IMPRESSION.gris,
    },
    cache: ENCRES_IMPRESSION.gris,
    remplissage: ENCRES_IMPRESSION.accent,
    marque: ENCRES_IMPRESSION.encre,
    texte: {
        nom: ENCRES_IMPRESSION.accent,
        etiquette: ENCRES_IMPRESSION.encre,
        cote: ENCRES_IMPRESSION.gris,
        graduation: ENCRES_IMPRESSION.gris,
        cote_arc: ENCRES_IMPRESSION.gris,
    },
    halo: ENCRES_IMPRESSION.fond,
    tons: TONS_IMPRESSION,
};
function completerPalette(p) {
    if (p === undefined)
        return PALETTE_IMPRESSION;
    return {
        calque: { ...PALETTE_IMPRESSION.calque, ...p.calque },
        cache: p.cache ?? PALETTE_IMPRESSION.cache,
        remplissage: p.remplissage ?? PALETTE_IMPRESSION.remplissage,
        marque: p.marque ?? PALETTE_IMPRESSION.marque,
        texte: { ...PALETTE_IMPRESSION.texte, ...p.texte },
        halo: p.halo ?? PALETTE_IMPRESSION.halo,
        tons: { ...TONS_IMPRESSION, ...p.tons },
    };
}
/* ======================================================= traits et textes */
/** Épaisseurs par calque, en unités de la liste (320 de large) — celles de l'application. */
export const EPAISSEUR_CALQUE_SVG = {
    grille: 1,
    axe: 1.4,
    figure: 2,
    courbe: 2.4,
    cote: 1.2,
};
/** Le style `fort` épaissit son calque d'un quart. */
const FACTEUR_FORT = 1.25;
/** La marque d'angle droit : un trait fin. */
const EPAISSEUR_CARRE_DROIT = 1.4;
/** Tirets d'un pointillé. */
const TIRETS = '5 4';
/** Largeur du liseré de fond autour d'un texte. */
const EPAISSEUR_HALO = 3;
/** Le remplissage d'un polygone : la figure se lit comme une surface, la grille reste visible dessous. */
export const OPACITE_REMPLISSAGE_SVG = 0.12;
/** La police sur la métrique de laquelle la liste a placé ses textes (`dimensionsTexteV2`), et des repli à chasse fixe. */
export const POLICE_FIGURE_SVG = "'IBM Plex Mono', ui-monospace, Menlo, Consolas, monospace";
const ANCRE = {
    debut: 'start',
    milieu: 'middle',
    fin: 'end',
};
function epaisseur(calque, style) {
    /* Une COURBE en tirets (frontière stricte d'une région, `fonction` en pointillé) garde
       l'épaisseur d'une courbe : seule la continuité du trait la distingue. */
    const fort = style === 'fort' || (calque === 'courbe' && style === 'pointille');
    return EPAISSEUR_CALQUE_SVG[calque] * (fort ? FACTEUR_FORT : 1);
}
function couleurTrait(pal, calque, style) {
    return calque === 'figure' && style === 'pointille' ? pal.cache : pal.calque[calque];
}
/** La couleur d'une primitive : celle de son ton s'il en porte un, sinon `defaut`. */
function teinte(pal, p, defaut) {
    return p.ton !== undefined && pal.tons !== undefined ? pal.tons[p.ton] : defaut;
}
/* ================================================================ écriture */
/** Échappe un texte ou une valeur d'attribut : rien de la figure ne devient du balisage. */
export function echapperXml(texte) {
    return texte
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
/** Deux décimales au plus ; jamais « -0 ». */
function n(v) {
    const r = Math.round(v * 100) / 100;
    return String(r === 0 ? 0 : r);
}
const points = (ps) => ps.map((p) => `${n(p.x)},${n(p.y)}`).join(' ');
const tirets = (style) => style === 'pointille' ? ` stroke-dasharray="${TIRETS}"` : '';
function primitiveVersSvg(p, pal, police) {
    switch (p.type) {
        case 'trait':
            return (`<line x1="${n(p.x1)}" y1="${n(p.y1)}" x2="${n(p.x2)}" y2="${n(p.y2)}" fill="none"` +
                ` stroke="${echapperXml(teinte(pal, p, couleurTrait(pal, p.calque, p.style)))}"` +
                ` stroke-width="${n(epaisseur(p.calque, p.style))}" stroke-linecap="round"${tirets(p.style)}/>`);
        case 'polyligne':
            return (`<polyline points="${points(p.points)}" fill="none"` +
                ` stroke="${echapperXml(teinte(pal, p, couleurTrait(pal, p.calque, p.style)))}"` +
                ` stroke-width="${n(epaisseur(p.calque, p.style))}" stroke-linecap="round"` +
                ` stroke-linejoin="round"${tirets(p.style)}/>`);
        case 'polygone':
            // `rempli` : une pointe de flèche (axe, cote), pleine, sans contour.
            if (p.rempli) {
                return `<polygon points="${points(p.points)}" fill="${echapperXml(teinte(pal, p, pal.calque[p.calque]))}" stroke="none"/>`;
            }
            return (`<polygon points="${points(p.points)}"` +
                /* `plein: false` : le contour seul — sa surface est peinte à part, ou pas du tout. */
                (p.calque === 'figure' && p.plein !== false
                    ? ` fill="${echapperXml(teinte(pal, p, pal.remplissage))}" fill-opacity="${OPACITE_REMPLISSAGE_SVG}"`
                    : ' fill="none"') +
                ` stroke="${echapperXml(teinte(pal, p, pal.calque[p.calque]))}"` +
                ` stroke-width="${n(epaisseur(p.calque, 'plein'))}" stroke-linejoin="round"/>`);
        case 'cercle':
            return (`<circle cx="${n(p.cx)}" cy="${n(p.cy)}" r="${n(p.r)}" fill="none"` +
                ` stroke="${echapperXml(teinte(pal, p, couleurTrait(pal, p.calque, p.style)))}"` +
                ` stroke-width="${n(epaisseur(p.calque, p.style))}"${tirets(p.style)}/>`);
        case 'marque':
            /* Un point VIDE : l'intérieur à la couleur du fond, opaque — le trait qui y aboutit s'arrête à son bord. */
            if (p.creux === true) {
                return (`<circle cx="${n(p.cx)}" cy="${n(p.cy)}" r="${n(p.r)}" fill="${echapperXml(pal.halo)}"` +
                    ` stroke="${echapperXml(teinte(pal, p, pal.marque))}" stroke-width="${n(EPAISSEUR_CREUX_V2)}"/>`);
            }
            return `<circle cx="${n(p.cx)}" cy="${n(p.cy)}" r="${n(p.r)}" fill="${echapperXml(teinte(pal, p, pal.marque))}"/>`;
        case 'arc': {
            /* La marque d'un angle n'a pas de style ; l'objet `arc` a le sien (plein, pointillé, fort). */
            const style = p.style ?? 'plein';
            return (`<path d="${echapperXml(p.d)}" fill="none"` +
                ` stroke="${echapperXml(teinte(pal, p, couleurTrait(pal, p.calque, style)))}"` +
                ` stroke-width="${n(epaisseur(p.calque, style) * (style === 'fort' ? FACTEUR_ARC_DESIGNE_V2 : 1))}"` +
                ` stroke-linecap="round"${tirets(style)}/>`);
        }
        case 'surface':
            /* Une surface sans contour. « fond » : opaque, à la couleur du fond — elle évide. */
            return p.remplissage === 'fond'
                ? `<path d="${echapperXml(p.d)}" fill="${echapperXml(pal.halo)}" stroke="none"/>`
                : `<path d="${echapperXml(p.d)}" fill="${echapperXml(teinte(pal, p, pal.remplissage))}"` +
                    ` fill-opacity="${OPACITE_REMPLISSAGE_SVG}" stroke="none"/>`;
        case 'carre_droit':
            return (`<polyline points="${points(p.points)}" fill="none" stroke="${echapperXml(teinte(pal, p, pal.cache))}"` +
                ` stroke-width="${EPAISSEUR_CARRE_DROIT}" stroke-linejoin="round"${tirets(p.style)}/>`);
        case 'texte': {
            const commun = `x="${n(p.x)}" y="${n(p.y)}" font-family="${echapperXml(police)}"` +
                ` font-size="${n(p.taille)}" text-anchor="${ANCRE[p.ancre]}"`;
            const texte = echapperXml(p.texte);
            // Deux passes, comme à l'écran : un liseré de la couleur du fond, puis
            // le texte. Une graduation posée sur la grille reste lisible sans boîte.
            return (`<text ${commun} fill="${echapperXml(pal.halo)}" stroke="${echapperXml(pal.halo)}"` +
                ` stroke-width="${EPAISSEUR_HALO}" stroke-linejoin="round" aria-hidden="true">${texte}</text>` +
                `<text ${commun} fill="${echapperXml(teinte(pal, p, pal.texte[p.role]))}">${texte}</text>`);
        }
        default: {
            // Union fermée : une primitive nouvelle casse la compilation ici.
            const jamais = p;
            return jamais;
        }
    }
}
/**
 * Une liste d'affichage en élément `<svg>`, dans son ordre de peinture. Le
 * `viewBox` est celui de la liste (il englobe tout, textes compris) ;
 * `preserveAspectRatio="xMidYMid meet"` garde la figure entière même si
 * l'appelant impose un cadre d'un autre rapport. L'attribut `data-taille` dit
 * si la figure est « partielle » ou « pleine ».
 */
export function listeVersSvg(liste, options = {}) {
    const pal = completerPalette(options.couleurs);
    const police = options.police ?? POLICE_FIGURE_SVG;
    const v = liste.viewBox;
    const dim = typeof options.dimensions === 'function' ? options.dimensions(liste) : options.dimensions;
    const taille = dim
        ? ` width="${echapperXml(dim.largeur)}"${dim.hauteur === undefined ? '' : ` height="${echapperXml(dim.hauteur)}"`}`
        : '';
    const classe = options.classe ? ` class="${echapperXml(options.classe)}"` : '';
    const fond = options.autonome
        ? `<rect x="${n(v.x)}" y="${n(v.y)}" width="${n(v.largeur)}" height="${n(v.hauteur)}"` +
            ` fill="${echapperXml(pal.halo)}"/>`
        : '';
    const corps = fond + liste.primitives.map((p) => primitiveVersSvg(p, pal, police)).join('');
    const xmlns = options.autonome ? ' xmlns="http://www.w3.org/2000/svg"' : '';
    return (`<svg${xmlns} viewBox="${n(v.x)} ${n(v.y)} ${n(v.largeur)} ${n(v.hauteur)}"${taille}` +
        ` preserveAspectRatio="xMidYMid meet" role="img"` +
        ` aria-label="${echapperXml(options.titre ?? 'Figure')}"${classe}` +
        ` data-taille="${liste.taille}">${corps}</svg>`);
}
/**
 * Une figure BRUTE (contrat « figure v2 » : `defis[].figure`, ou la
 * spécification d'une figure lue sur une photo) en `<svg>` : normalisée,
 * dessinée par le moteur (plan ou espace — `vue` choisit le point de vue d'un
 * solide), puis recopiée. Une figure refusée par le moteur n'est jamais
 * dessinée à moitié : ses griefs sont rendus à la place.
 */
export function figureVersSvg(figure, options = {}) {
    const r = normaliserFigureV2(figure);
    if ('griefs' in r)
        return { griefs: r.griefs };
    const liste = r.figure.espace === 'espace' ? dessinerEspace(r.figure, options.vue) : dessinerPlan(r.figure);
    return { svg: listeVersSvg(liste, options), liste };
}
//# sourceMappingURL=svg-texte.js.map